import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../atoms/button';
import { Input } from '../atoms/input';
import { Label } from '../atoms/label';
import Icon from '../molecules/Icon';
import RichTextEditor, { RichTextEditorRef } from '../molecules/RichTextEditor';
import { showToast } from '../molecules/toast-helpers';
import { IndustryMultiSelect } from '../molecules/IndustryMultiSelect';
import { LoaderCircle, FileText, X } from 'lucide-react';

/** Accepted types for the tone-of-voice / content-source dropzone (mirrors onboarding). */
const CONTENT_SOURCE_ACCEPT =
  '.md,.markdown,.csv,.xls,.xlsx,text/markdown,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export interface StartupInfoFormData {
  name: string;
  industry: string;
  target_icp: string;
  problem_details: string;
}

export interface StartupInfoSubmit extends StartupInfoFormData {
  /**
   * Tone-of-voice source files (.md / .csv / .xls / .xlsx). Uploaded with
   * `extract_tone=true` so the AI-API learns the product's tone — same as the
   * onboarding "Attach content source file" step.
   */
  contentSourceFiles?: File[];
}

export interface StartupInfoFormProps {
  initialValues?: Partial<StartupInfoFormData>;
  isUpdating?: boolean;
  isWeb?: boolean;
  mode?: 'create' | 'update';
  onSubmit: (data: StartupInfoSubmit) => Promise<void>;
  onCancel?: () => void;
  /** When true, hides the internal action row — caller provides its own (e.g. drawer footer). */
  hideActions?: boolean;
  className?: string;
}

const FORM_ID = 'startup-info-form';

export const StartupInfoForm: React.FC<StartupInfoFormProps> = ({
  initialValues = {},
  isUpdating = false,
  isWeb = false,
  mode = 'update',
  onSubmit,
  onCancel,
  hideActions = false,
  className,
}) => {
  const { t } = useTranslation();
  const isCreate = mode === 'create';
  const [formData, setFormData] = useState<StartupInfoFormData>({
    name: '',
    industry: '',
    target_icp: '',
    problem_details: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [contentSourceFiles, setContentSourceFiles] = useState<File[]>([]);
  const originalRef = useRef<StartupInfoFormData | null>(null);
  const editorRef = useRef<RichTextEditorRef>(null);
  const contentFileInputRef = useRef<HTMLInputElement>(null);

  // Reset form whenever the initialValues identity changes (e.g. switching startups).
  // Callers MUST memoize `initialValues` — passing a fresh object literal on every
  // render will fire this effect on each keystroke and wipe user input.
  useEffect(() => {
    const initialized: StartupInfoFormData = {
      name: initialValues.name ?? '',
      industry: initialValues.industry ?? '',
      target_icp: initialValues.target_icp ?? '',
      problem_details: initialValues.problem_details ?? '',
    };
    setFormData(initialized);
    originalRef.current = initialized;
    setContentSourceFiles([]);
    // Don't imperatively setContent — Quill takes `formData.problem_details`
    // via the controlled `initialValue` prop below. The two paths (mutating
    // innerHTML vs. React's value prop) race and can leave Quill's Delta
    // out of sync with the DOM.
  }, [initialValues]);

  const hasChanges = useMemo(() => {
    if (!originalRef.current) return false;
    return (Object.keys(formData) as (keyof StartupInfoFormData)[]).some(
      (k) => formData[k] !== originalRef.current![k]
    );
  }, [formData]);

  const handleChange = (field: keyof StartupInfoFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleProblemDetailsChange = (content: string) => {
    setFormData((prev) => ({ ...prev, problem_details: content }));
  };

  const addContentSourceFiles = (incoming: FileList | File[] | null | undefined) => {
    if (!incoming) return;
    setContentSourceFiles((prev) => [...prev, ...Array.from(incoming)]);
  };

  const removeContentSourceFile = (idx: number) => {
    setContentSourceFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name?.trim() || isLoading || isUpdating) return;
    setIsLoading(true);
    try {
      const rawProblem = editorRef.current?.getContent() ?? formData.problem_details;
      const isEmptyRichText = (html: string) =>
        !html || !html.replace(/<[^>]+>/g, '').replace(/&nbsp;|\s+/g, '').length;
      const data: StartupInfoSubmit = {
        ...formData,
        problem_details: isEmptyRichText(rawProblem) ? '' : rawProblem,
        ...(contentSourceFiles.length > 0 && { contentSourceFiles }),
      };
      await onSubmit(data);
      originalRef.current = { ...formData, problem_details: data.problem_details };
      setContentSourceFiles([]);
      showToast({
        message: isCreate ? t('startupForm.createdToast') : t('startupForm.updatedToast'),
        severity: 'success',
      });
    } catch (err) {
      console.error(isCreate ? 'Failed to create startup:' : 'Failed to update startup info:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const isBusy = isLoading || isUpdating;
  const submitDisabled =
    isBusy ||
    !formData.name?.trim() ||
    (!isCreate && !hasChanges && contentSourceFiles.length === 0);

  return (
    <div className={className}>
      <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="startup-name">{t('startupForm.name')}</Label>
          <Input
            id="startup-name"
            value={formData.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange('name', e.target.value)
            }
            placeholder={t('startupForm.namePlaceholder')}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('startupForm.industry')}</Label>
          <IndustryMultiSelect
            value={formData.industry}
            onChange={(v) => handleChange('industry', v)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="target-icp">{t('startupForm.targetIcp')}</Label>
          <Input
            id="target-icp"
            value={formData.target_icp}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange('target_icp', e.target.value)
            }
            placeholder={t('startupForm.targetIcpPlaceholder')}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('startupForm.problem')}</Label>
          <div className="min-h-[120px]">
            <RichTextEditor
              ref={editorRef}
              initialValue={formData.problem_details}
              placeholder={t('startupForm.problemPlaceholder')}
              onChange={handleProblemDetailsChange}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('startupForm.attachTitle')}</Label>
          <button
            type="button"
            onClick={() => contentFileInputRef.current?.click()}
            className="flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-gray-300 px-6 py-8 text-center transition-colors hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800/50"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-400 dark:border-gray-700">
              <Icon icon={FileText} size={18} />
            </span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('startupForm.clickToAttach')}
            </span>
            <span className="max-w-md text-xs text-gray-500 dark:text-gray-400">
              {t('startupForm.attachHint')}
            </span>
          </button>
          <input
            ref={contentFileInputRef}
            type="file"
            multiple
            hidden
            accept={CONTENT_SOURCE_ACCEPT}
            onChange={(e) => {
              addContentSourceFiles(e.target.files);
              e.target.value = '';
            }}
          />
          {contentSourceFiles.length > 0 && (
            <ul className="space-y-2">
              {contentSourceFiles.map((file, idx) => (
                <li
                  key={`${file.name}-${idx}`}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/40"
                >
                  <Icon icon={FileText} size={16} className="shrink-0 text-gray-400" />
                  <span className="flex-1 truncate text-sm text-gray-700 dark:text-gray-200">
                    {file.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeContentSourceFile(idx)}
                    className="text-gray-400 transition-colors hover:text-red-500"
                    aria-label={t('startupForm.removeFile', { name: file.name })}
                  >
                    <Icon icon={X} size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </form>

      {!hideActions && (
        <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
          {onCancel && (
            <Button variant="secondary-gray" size={isWeb ? 'sm' : 'xs'} onClick={onCancel}>
              {t('startupForm.cancel')}
            </Button>
          )}
          <Button
            variant="primary"
            size={isWeb ? 'sm' : 'xs'}
            type="submit"
            form={FORM_ID}
            disabled={submitDisabled}
          >
            {isBusy && (
              <Icon
                icon={LoaderCircle}
                size={18}
                strokeWidth={2}
                color="#fff"
                className="animate-spin mr-2"
              />
            )}
            {isBusy
              ? isCreate
                ? t('startupForm.creating')
                : t('startupForm.updating')
              : isCreate
                ? t('startupForm.create')
                : t('startupForm.update')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default StartupInfoForm;
